import { useEffect, useMemo, useRef, useState } from 'react'
import { Button, Modal, Result, Spin } from 'antd'
import CIMSDrawer from 'components/CIMSDrawer'
import WorkflowRecordFileManager from 'components/WorkflowRecordFileManager'
import {
  createWorkflowFileOperations,
  workflowFileRequestError,
} from 'utils/workflowRecordFiles'
import type { WorkflowFileMetadata } from '@cims-online/shared'
import type { ManageWorkflowRecordFilesDrawerProps } from './types'

/**
 * Drawer for managing one record's SharePoint files.
 *
 * Each keyed mount loads the record's folder metadata and confirms before
 * closing while uploads or removals are in progress. The record remains mounted
 * until the drawer's exit animation finishes.
 */
function ManageWorkflowRecordFilesDrawer({
  projectId,
  recordId,
  onClose,
  onChanged,
}: ManageWorkflowRecordFilesDrawerProps) {
  const [open, setOpen] = useState(true)
  const [metadata, setMetadata] = useState<WorkflowFileMetadata | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [attempt, setAttempt] = useState(0)
  const busy = useRef(false)
  const operations = useMemo(
    () => createWorkflowFileOperations(projectId, recordId),
    [projectId, recordId]
  )
  const changed = useRef(onChanged)
  changed.current = onChanged
  useEffect(() => {
    const abort = new AbortController()
    operations
      .metadata(abort.signal)
      .then((result) => {
        if (!abort.signal.aborted) {
          setMetadata(result)
          setError(null)
        }
      })
      .catch((failure) => {
        if (!abort.signal.aborted) {
          setError(workflowFileRequestError(failure).message)
          // Availability may have changed; reload the table's actions.
          changed.current()
        }
      })
    return () => abort.abort()
  }, [operations, attempt])
  /** Defers parent cleanup until the drawer has animated closed. */
  const close = () => {
    if (busy.current)
      Modal.confirm({
        title: 'Close file manager?',
        content:
          'Incomplete uploads will be cancelled. Files already uploaded and folders already created remain in SharePoint.',
        okText: 'Cancel uploads and close',
        onOk: () => setOpen(false),
      })
    else setOpen(false)
  }
  let title = 'Manage Files'
  if (metadata)
    title = `Manage Files for ${metadata.renderedLabel ?? metadata.root.name}`
  return (
    <CIMSDrawer
      open={open}
      title={title}
      onClose={close}
      afterOpenChange={(visible) => {
        if (!visible) onClose()
      }}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={close}>Close</Button>
        </div>
      }
    >
      <div style={{ height: '100%', overflowY: 'auto' }}>
        {error && (
          <Result
            status="error"
            title="Unable to manage files"
            subTitle={error}
            extra={
              <Button onClick={() => setAttempt((value) => value + 1)}>
                Retry
              </Button>
            }
          />
        )}
        {!metadata && !error && (
          <div
            style={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Spin tip="Loading file manager…" />
          </div>
        )}
        {metadata && (
          <WorkflowRecordFileManager
            projectId={projectId}
            recordId={recordId}
            metadata={metadata}
            onDenied={onChanged}
            onBusyChange={(value) => {
              busy.current = value
            }}
          />
        )}
      </div>
    </CIMSDrawer>
  )
}
export default ManageWorkflowRecordFilesDrawer
